import React, { useState } from 'react';
import { Mail, CheckCircle, BookOpen, Clock, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailCourseSignup = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrolled, setEnrolled] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/course/enroll`, { email, name });
      if (response.data.success) {
        setEnrolled(true);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const courseHighlights = [
    { day: 1, title: "Pre-Trip Inspection Essentials" },
    { day: 2, title: "Defensive Driving Techniques" },
    { day: 3, title: "Fatigue Management Strategies" },
    { day: 4, title: "Weather Driving Mastery" },
    { day: 5, title: "Load Securement Best Practices" },
    { day: 6, title: "Accident Response Protocol" },
    { day: 7, title: "Building Lifelong Safety Habits" },
  ];

  if (enrolled) {
    return (
      <Card className="bg-gradient-to-br from-green-900/50 to-green-800/30 border-green-500/50">
        <CardContent className="p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">You're Enrolled!</h3>
          <p className="text-gray-300">
            Check your inbox for Day 1 of the 7-Day Truck Safety Course.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-orange-900/30 to-gray-900 border-orange-500/30 overflow-hidden">
      <CardContent className="p-0">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Left side - Course info */}
          <div className="p-8 bg-gradient-to-br from-orange-600 to-orange-700">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="h-6 w-6 text-white" />
              <span className="text-sm font-semibold text-orange-100 uppercase tracking-wider">Free Email Course</span>
            </div>
            <h3 className="text-3xl font-bold text-white mb-3">
              7-Day Truck Safety Course
            </h3>
            <p className="text-orange-100 mb-6">
              Master essential safety skills with daily lessons delivered to your inbox. Join thousands of drivers who've improved their safety record.
            </p>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-orange-100">
                <Clock className="h-4 w-4" />
                <span className="text-sm">5 min/day</span>
              </div>
              <div className="flex items-center gap-2 text-orange-100">
                <Shield className="h-4 w-4" />
                <span className="text-sm">100% Free</span>
              </div>
            </div>

            <div className="space-y-2">
              {courseHighlights.map((item) => (
                <div key={item.day} className="flex items-center gap-3 text-sm text-orange-50">
                  <span className="w-6 h-6 rounded-full bg-orange-500/50 flex items-center justify-center text-xs font-bold">
                    {item.day}
                  </span>
                  <span>{item.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Signup form */}
          <div className="p-8 flex flex-col justify-center">
            <h4 className="text-xl font-semibold text-white mb-2">
              Start Your Free Course
            </h4>
            <p className="text-gray-400 mb-6 text-sm">
              Enter your email to receive daily safety lessons. Unsubscribe anytime.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3"
                disabled={loading}
              >
                {loading ? (
                  'Enrolling...'
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Start Free Course
                  </>
                )}
              </Button>
            </form>

            <p className="text-xs text-gray-500 mt-4 text-center">
              By enrolling, you agree to receive emails from TruckAccidentSupport.
              <br />We respect your privacy and never spam.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EmailCourseSignup;
